import React, { useEffect, useMemo, useState } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import {
  ActivityIndicator,
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  List,
  Snackbar,
  Text,
  TextInput,
} from "react-native-paper";
import { useTranslation } from "react-i18next";
import { getConfig } from "../../config/config";
import {
  applyRoleConfig,
  CompanyRoleConfig,
  CompanyRolePermissions,
  extractRoleConfig,
} from "../../config/companies/roleConfig";
import {
  getCompanyRoleConfig,
  saveCompanyRoleConfig,
} from "../../services/companyConfigService";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { setConfig } from "../../redux/slices/authSlice";
import { StatusOption } from "../../config/companies/config";

type PermissionArrayKey =
  | "list"
  | "form"
  | "formCreate"
  | "formEdit"
  | "board"
  | "details"
  | "showAdd";

type FieldPermissions = NonNullable<
  NonNullable<
    NonNullable<CompanyRolePermissions["collections"]>[string]["fields"]
  >[string]
>;

type ScreenPermissions = NonNullable<
  NonNullable<CompanyRolePermissions["screens"]>[string]
>;

type CollectionPermissions = NonNullable<
  NonNullable<CompanyRolePermissions["collections"]>[string]
>;

const uniqueRoleName = (roles: string[]) => {
  let index = roles.length + 1;
  let role = `role${index}`;

  while (roles.includes(role)) {
    index += 1;
    role = `role${index}`;
  }

  return role;
};

const toggleRole = (selected: string[] = [], role: string) =>
  selected.includes(role)
    ? selected.filter((item) => item !== role)
    : [...selected, role];

const replaceRoleInArray = (
  selected: string[] | undefined,
  oldRole: string,
  newRole: string,
) => {
  if (!selected) return selected;

  return Array.from(
    new Set(selected.map((role) => (role === oldRole ? newRole : role))),
  );
};

const removeRoleFromArray = (selected: string[] | undefined, role: string) =>
  selected?.filter((item) => item !== role);

const mapStatusOptions = (
  options: StatusOption[] | undefined,
  mapper: (roles: string[] | undefined) => string[] | undefined,
) =>
  options?.map((option) => ({
    ...option,
    editableBy: mapper(option.editableBy) ?? [],
    visibleBy: mapper(option.visibleBy) ?? [],
  }));

const mapRoleArrays = (
  roleConfig: CompanyRoleConfig,
  mapper: (roles: string[] | undefined) => string[] | undefined,
): CompanyRoleConfig => {
  const mappedDefaultRole = roleConfig.defaultRole
    ? mapper([roleConfig.defaultRole])?.[0]
    : undefined;

  return {
    ...roleConfig,
    roles: mapper(roleConfig.roles) ?? [],
    defaultRole: mappedDefaultRole ?? roleConfig.defaultRole,
    permissions: {
      ...roleConfig.permissions,
      employeesVisibleTo: mapper(roleConfig.permissions.employeesVisibleTo),
      employeesEditableBy: mapper(roleConfig.permissions.employeesEditableBy),
      screens: Object.fromEntries(
        Object.entries(roleConfig.permissions.screens ?? {}).map(
          ([screenName, permissions]) => [
            screenName,
            {
              ...permissions,
              roles: mapper(permissions.roles),
              showAdd: mapper(permissions.showAdd),
              reordering: mapper(permissions.reordering),
              statusOptions: mapStatusOptions(
                permissions.statusOptions,
                mapper,
              ),
            },
          ],
        ),
      ),
      collections: Object.fromEntries(
        Object.entries(roleConfig.permissions.collections ?? {}).map(
          ([collectionName, permissions]) => [
            collectionName,
            {
              ...permissions,
              deletableBy: mapper(permissions.deletableBy),
              activityLogs: permissions.activityLogs
                ? {
                    ...permissions.activityLogs,
                    roles: mapper(permissions.activityLogs.roles),
                  }
                : undefined,
              fields: Object.fromEntries(
                Object.entries(permissions.fields ?? {}).map(
                  ([fieldName, fieldPermissions]) => [
                    fieldName,
                    {
                      ...fieldPermissions,
                      list: mapper(fieldPermissions.list),
                      form: mapper(fieldPermissions.form),
                      formCreate: mapper(fieldPermissions.formCreate),
                      formEdit: mapper(fieldPermissions.formEdit),
                      board: mapper(fieldPermissions.board),
                      details: mapper(fieldPermissions.details),
                      showAdd: mapper(fieldPermissions.showAdd),
                      statusOptions: mapStatusOptions(
                        fieldPermissions.statusOptions,
                        mapper,
                      ),
                    },
                  ],
                ),
              ),
            },
          ],
        ),
      ),
    },
  };
};

const RoleSelector = ({
  title,
  roles,
  selected,
  onChange,
}: {
  title: string;
  roles: string[];
  selected?: string[];
  onChange: (roles: string[]) => void;
}) => (
  <View style={styles.permissionBlock}>
    <View style={styles.permissionHeader}>
      <Text variant="labelLarge">{title}</Text>
      <View style={styles.permissionActions}>
        <Button compact onPress={() => onChange([...roles])}>
          All
        </Button>
        <Button compact onPress={() => onChange([])}>
          None
        </Button>
      </View>
    </View>
    <View style={styles.chips}>
      {roles.map((role) => {
        const isSelected = !!selected?.includes(role);

        return (
          <Chip
            key={role}
            selected={isSelected}
            mode={isSelected ? "flat" : "outlined"}
            onPress={() => onChange(toggleRole(selected, role))}
            style={styles.chip}
          >
            {role}
          </Chip>
        );
      })}
    </View>
  </View>
);

export default function RolePermissionsScreen() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const companyId = user.company?.id;
  const companyType = user.company?.type;
  const baseConfig = useMemo(() => getConfig(companyType), [companyType]);
  const [roleConfig, setRoleConfig] = useState<CompanyRoleConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ visible: false, message: "" });

  useEffect(() => {
    let cancelled = false;

    const loadRoleConfig = async () => {
      try {
        setLoading(true);
        const backendRoleConfig = await getCompanyRoleConfig(companyId);
        if (!cancelled) {
          setRoleConfig(backendRoleConfig ?? extractRoleConfig(baseConfig));
        }
      } catch (error) {
        console.error("Failed to load role permissions:", error);
        if (!cancelled) {
          setSnackbar({
            visible: true,
            message: "Failed to load role permissions",
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadRoleConfig();

    return () => {
      cancelled = true;
    };
  }, [baseConfig, companyId]);

  const updateRoleConfig = (updater: (config: CompanyRoleConfig) => void) => {
    setRoleConfig((current) => {
      if (!current) return current;

      const next = JSON.parse(JSON.stringify(current)) as CompanyRoleConfig;
      updater(next);
      return next;
    });
  };

  const addRole = () => {
    updateRoleConfig((config) => {
      config.roles.push(uniqueRoleName(config.roles));
    });
  };

  const renameRole = (index: number, newRole: string) => {
    setRoleConfig((current) => {
      if (!current) return current;

      const oldRole = current.roles[index];
      const nextRoleConfig = mapRoleArrays(current, (roles) =>
        replaceRoleInArray(roles, oldRole, newRole.trim()),
      );
      nextRoleConfig.roles[index] = newRole.trim();
      return nextRoleConfig;
    });
  };

  const removeRole = (role: string) => {
    setRoleConfig((current) => {
      if (!current) return current;

      const nextRoleConfig = mapRoleArrays(current, (roles) =>
        removeRoleFromArray(roles, role),
      );

      if (nextRoleConfig.defaultRole === role) {
        nextRoleConfig.defaultRole = nextRoleConfig.roles[0];
      }

      return nextRoleConfig;
    });
  };

  const validate = () => {
    if (!roleConfig) return "Role config is not loaded";
    if (!companyId) return "Company is not selected";
    if (roleConfig.roles.length === 0) return "At least one role is required";
    if (roleConfig.roles.some((role) => !role.trim())) {
      return "Roles cannot be empty";
    }
    if (new Set(roleConfig.roles).size !== roleConfig.roles.length) {
      return "Roles must be unique";
    }
    if (
      roleConfig.defaultRole &&
      !roleConfig.roles.includes(roleConfig.defaultRole)
    ) {
      return "Default role must be in roles list";
    }

    return null;
  };

  const save = async () => {
    const error = validate();
    if (error) {
      setSnackbar({ visible: true, message: error });
      return;
    }

    if (!roleConfig || !companyId) return;

    try {
      setSaving(true);
      await saveCompanyRoleConfig(companyId, roleConfig);
      dispatch(setConfig(applyRoleConfig(baseConfig, roleConfig)));
      setSnackbar({ visible: true, message: "Role permissions saved" });
    } catch (saveError) {
      console.error("Failed to save role permissions:", saveError);
      setSnackbar({ visible: true, message: "Failed to save permissions" });
    } finally {
      setSaving(false);
    }
  };

  const ensureScreenPermissions = (screenName: string): ScreenPermissions => {
    const screen = baseConfig.screens.find((item) => item.name === screenName);
    return (
      roleConfig?.permissions.screens?.[screenName] ?? {
        roles: screen?.roles ?? [],
        showAdd: screen?.showAdd,
        reordering: screen?.reordering,
        statusOptions: screen?.statusOptions,
      }
    );
  };

  const ensureCollectionPermissions = (
    collectionName: string,
  ): CollectionPermissions => {
    const collection = baseConfig.database.find(
      (item) => item.name === collectionName,
    );
    return (
      roleConfig?.permissions.collections?.[collectionName] ?? {
        deletableBy: collection?.deletableBy,
        activityLogs: collection?.activityLogs
          ? { roles: collection.activityLogs.roles }
          : undefined,
        fields: {},
      }
    );
  };

  const ensureFieldPermissions = (
    collectionName: string,
    fieldName: string,
  ): FieldPermissions => {
    const collection = baseConfig.database.find(
      (item) => item.name === collectionName,
    );
    const field = collection?.fields.find((item) => item.name === fieldName);
    return (
      roleConfig?.permissions.collections?.[collectionName]?.fields?.[
        fieldName
      ] ?? {
        list: field?.list ?? [],
        form: field?.form,
        formCreate: field?.formCreate,
        formEdit: field?.formEdit,
        board: field?.board,
        details: field?.details,
        showAdd: field?.showAdd,
        statusOptions: field?.statusOptions,
      }
    );
  };

  const setTopLevelRoles = (
    key: "employeesVisibleTo" | "employeesEditableBy",
    roles: string[],
  ) => {
    updateRoleConfig((config) => {
      config.permissions[key] = roles;
    });
  };

  const setScreenRoles = (
    screenName: string,
    key: "roles" | "showAdd" | "reordering",
    roles: string[],
  ) => {
    updateRoleConfig((config) => {
      config.permissions.screens = config.permissions.screens ?? {};
      config.permissions.screens[screenName] = {
        ...ensureScreenPermissions(screenName),
        [key]: roles,
      };
    });
  };

  const setCollectionRoles = (
    collectionName: string,
    key: "deletableBy",
    roles: string[],
  ) => {
    updateRoleConfig((config) => {
      config.permissions.collections = config.permissions.collections ?? {};
      config.permissions.collections[collectionName] = {
        ...ensureCollectionPermissions(collectionName),
        [key]: roles,
      };
    });
  };

  const setActivityLogRoles = (collectionName: string, roles: string[]) => {
    updateRoleConfig((config) => {
      config.permissions.collections = config.permissions.collections ?? {};
      const collectionPermissions = ensureCollectionPermissions(collectionName);
      config.permissions.collections[collectionName] = {
        ...collectionPermissions,
        activityLogs: {
          ...collectionPermissions.activityLogs,
          roles,
        },
      };
    });
  };

  const setFieldRoles = (
    collectionName: string,
    fieldName: string,
    key: PermissionArrayKey,
    roles: string[],
  ) => {
    updateRoleConfig((config) => {
      config.permissions.collections = config.permissions.collections ?? {};
      const collectionPermissions = ensureCollectionPermissions(collectionName);
      const fields = collectionPermissions.fields ?? {};

      config.permissions.collections[collectionName] = {
        ...collectionPermissions,
        fields: {
          ...fields,
          [fieldName]: {
            ...ensureFieldPermissions(collectionName, fieldName),
            [key]: roles,
          },
        },
      };
    });
  };

  const setStatusOptionRoles = (
    statusOptionsOwner:
      | { type: "screen"; screenName: string }
      | { type: "field"; collectionName: string; fieldName: string },
    statusIndex: number,
    key: "editableBy" | "visibleBy",
    roles: string[],
  ) => {
    updateRoleConfig((config) => {
      if (statusOptionsOwner.type === "screen") {
        config.permissions.screens = config.permissions.screens ?? {};
        const screenPermissions = ensureScreenPermissions(
          statusOptionsOwner.screenName,
        );
        const statusOptions = [...(screenPermissions.statusOptions ?? [])];
        statusOptions[statusIndex] = {
          ...statusOptions[statusIndex],
          [key]: roles,
        };
        config.permissions.screens[statusOptionsOwner.screenName] = {
          ...screenPermissions,
          statusOptions,
        };
        return;
      }

      config.permissions.collections = config.permissions.collections ?? {};
      const collectionPermissions = ensureCollectionPermissions(
        statusOptionsOwner.collectionName,
      );
      const fields = collectionPermissions.fields ?? {};
      const fieldPermissions = ensureFieldPermissions(
        statusOptionsOwner.collectionName,
        statusOptionsOwner.fieldName,
      );
      const statusOptions = [...(fieldPermissions.statusOptions ?? [])];
      statusOptions[statusIndex] = {
        ...statusOptions[statusIndex],
        [key]: roles,
      };

      config.permissions.collections[statusOptionsOwner.collectionName] = {
        ...collectionPermissions,
        fields: {
          ...fields,
          [statusOptionsOwner.fieldName]: {
            ...fieldPermissions,
            statusOptions,
          },
        },
      };
    });
  };

  if (loading || !roleConfig) {
    return (
      <View style={styles.loading}>
        <ActivityIndicator size="large" />
        <Text>Loading role permissions...</Text>
      </View>
    );
  }

  const roles = roleConfig.roles;

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.content}>
        <Card style={styles.card}>
          <Card.Content>
            <View style={styles.sectionHeader}>
              <View>
                <Text variant="titleLarge">Roles</Text>
                <Text variant="bodySmall" style={styles.muted}>
                  {user.company?.name}
                </Text>
              </View>
              <Button mode="outlined" icon="plus" onPress={addRole}>
                Add
              </Button>
            </View>

            {roles.map((role, index) => (
              <View key={`${role}-${index}`} style={styles.roleRow}>
                <TextInput
                  label="Role"
                  value={role}
                  mode="outlined"
                  dense
                  style={styles.roleInput}
                  onChangeText={(value) => renameRole(index, value)}
                />
                <IconButton
                  icon="delete-outline"
                  iconColor="#d32f2f"
                  disabled={roles.length <= 1}
                  onPress={() => removeRole(role)}
                />
              </View>
            ))}

            <Divider style={styles.divider} />
            <Text variant="labelLarge">Default role</Text>
            <View style={styles.chips}>
              {roles.map((role) => (
                <Chip
                  key={role}
                  selected={roleConfig.defaultRole === role}
                  mode={roleConfig.defaultRole === role ? "flat" : "outlined"}
                  onPress={() =>
                    updateRoleConfig((config) => {
                      config.defaultRole = role;
                    })
                  }
                  style={styles.chip}
                >
                  {role}
                </Chip>
              ))}
            </View>
          </Card.Content>
        </Card>

        <Card style={styles.card}>
          <Card.Content>
            <Text variant="titleLarge">Employees</Text>
            <RoleSelector
              title="Can see employees"
              roles={roles}
              selected={roleConfig.permissions.employeesVisibleTo}
              onChange={(nextRoles) =>
                setTopLevelRoles("employeesVisibleTo", nextRoles)
              }
            />
            <RoleSelector
              title="Can edit employees"
              roles={roles}
              selected={roleConfig.permissions.employeesEditableBy}
              onChange={(nextRoles) =>
                setTopLevelRoles("employeesEditableBy", nextRoles)
              }
            />
          </Card.Content>
        </Card>

        <Card style={styles.card}>
          <Card.Content>
            <Text variant="titleLarge">Screens</Text>
            {baseConfig.screens.map((screen) => {
              const screenPermissions = ensureScreenPermissions(screen.name);

              return (
                <List.Accordion
                  key={screen.name}
                  title={t(screen.name)}
                  description={screen.name}
                  left={(props) => <List.Icon {...props} icon="view-list" />}
                >
                  <View style={styles.accordionBody}>
                    <RoleSelector
                      title="Can open screen"
                      roles={roles}
                      selected={screenPermissions.roles}
                      onChange={(nextRoles) =>
                        setScreenRoles(screen.name, "roles", nextRoles)
                      }
                    />
                    {screen.showAdd !== undefined && (
                      <RoleSelector
                        title="Can create"
                        roles={roles}
                        selected={screenPermissions.showAdd}
                        onChange={(nextRoles) =>
                          setScreenRoles(screen.name, "showAdd", nextRoles)
                        }
                      />
                    )}
                    {screen.reordering !== undefined && (
                      <RoleSelector
                        title="Can reorder"
                        roles={roles}
                        selected={screenPermissions.reordering}
                        onChange={(nextRoles) =>
                          setScreenRoles(screen.name, "reordering", nextRoles)
                        }
                      />
                    )}
                    {screenPermissions.statusOptions?.map(
                      (statusOption, statusIndex) => (
                        <View
                          key={`${statusOption.value}-${statusIndex}`}
                          style={styles.statusBlock}
                        >
                          <Text variant="labelLarge">
                            Status: {String(statusOption.value)}
                          </Text>
                          <RoleSelector
                            title="Can set status"
                            roles={roles}
                            selected={statusOption.editableBy}
                            onChange={(nextRoles) =>
                              setStatusOptionRoles(
                                { type: "screen", screenName: screen.name },
                                statusIndex,
                                "editableBy",
                                nextRoles,
                              )
                            }
                          />
                          <RoleSelector
                            title="Can see status"
                            roles={roles}
                            selected={statusOption.visibleBy}
                            onChange={(nextRoles) =>
                              setStatusOptionRoles(
                                { type: "screen", screenName: screen.name },
                                statusIndex,
                                "visibleBy",
                                nextRoles,
                              )
                            }
                          />
                        </View>
                      ),
                    )}
                  </View>
                </List.Accordion>
              );
            })}
          </Card.Content>
        </Card>

        <Card style={styles.card}>
          <Card.Content>
            <Text variant="titleLarge">Collections and fields</Text>
            {baseConfig.database.map((collection) => {
              const collectionPermissions = ensureCollectionPermissions(
                collection.name,
              );

              return (
                <List.Accordion
                  key={collection.name}
                  title={collection.name}
                  left={(props) => <List.Icon {...props} icon="database" />}
                >
                  <View style={styles.accordionBody}>
                    {collection.deletableBy !== undefined && (
                      <RoleSelector
                        title="Can delete records"
                        roles={roles}
                        selected={collectionPermissions.deletableBy}
                        onChange={(nextRoles) =>
                          setCollectionRoles(
                            collection.name,
                            "deletableBy",
                            nextRoles,
                          )
                        }
                      />
                    )}
                    {collection.activityLogs && (
                      <RoleSelector
                        title="Can see activity logs"
                        roles={roles}
                        selected={collectionPermissions.activityLogs?.roles}
                        onChange={(nextRoles) =>
                          setActivityLogRoles(collection.name, nextRoles)
                        }
                      />
                    )}

                    {collection.fields.map((field) => {
                      const fieldPermissions = ensureFieldPermissions(
                        collection.name,
                        field.name,
                      );

                      return (
                        <List.Accordion
                          key={field.name}
                          title={t(field.label)}
                          description={field.name}
                          left={(props) => (
                            <List.Icon {...props} icon="form-textbox" />
                          )}
                        >
                          <View style={styles.fieldBody}>
                            {(
                              [
                                ["list", "List"],
                                ["form", "Form"],
                                ["formCreate", "Create form"],
                                ["formEdit", "Edit form"],
                                ["board", "Board"],
                                ["details", "Details"],
                                ["showAdd", "Can add related"],
                              ] as [PermissionArrayKey, string][]
                            ).map(([key, label]) => {
                              if (field[key] === undefined) {
                                return null;
                              }

                              return (
                                <RoleSelector
                                  key={key}
                                  title={label}
                                  roles={roles}
                                  selected={fieldPermissions[key]}
                                  onChange={(nextRoles) =>
                                    setFieldRoles(
                                      collection.name,
                                      field.name,
                                      key,
                                      nextRoles,
                                    )
                                  }
                                />
                              );
                            })}

                            {fieldPermissions.statusOptions?.map(
                              (statusOption, statusIndex) => (
                                <View
                                  key={`${statusOption.value}-${statusIndex}`}
                                  style={styles.statusBlock}
                                >
                                  <Text variant="labelLarge">
                                    Status: {String(statusOption.value)}
                                  </Text>
                                  <RoleSelector
                                    title="Can set status"
                                    roles={roles}
                                    selected={statusOption.editableBy}
                                    onChange={(nextRoles) =>
                                      setStatusOptionRoles(
                                        {
                                          type: "field",
                                          collectionName: collection.name,
                                          fieldName: field.name,
                                        },
                                        statusIndex,
                                        "editableBy",
                                        nextRoles,
                                      )
                                    }
                                  />
                                  <RoleSelector
                                    title="Can see status"
                                    roles={roles}
                                    selected={statusOption.visibleBy}
                                    onChange={(nextRoles) =>
                                      setStatusOptionRoles(
                                        {
                                          type: "field",
                                          collectionName: collection.name,
                                          fieldName: field.name,
                                        },
                                        statusIndex,
                                        "visibleBy",
                                        nextRoles,
                                      )
                                    }
                                  />
                                </View>
                              ),
                            )}
                          </View>
                        </List.Accordion>
                      );
                    })}
                  </View>
                </List.Accordion>
              );
            })}
          </Card.Content>
        </Card>
      </ScrollView>

      <View style={styles.footer}>
        <Button
          mode="contained"
          icon="content-save-outline"
          loading={saving}
          disabled={saving}
          onPress={save}
        >
          Save permissions
        </Button>
      </View>

      <Snackbar
        visible={snackbar.visible}
        onDismiss={() => setSnackbar({ visible: false, message: "" })}
        duration={3000}
      >
        {snackbar.message}
      </Snackbar>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#f5f5f5",
  },
  loading: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  },
  content: {
    width: "100%",
    maxWidth: 960,
    alignSelf: "center",
    padding: 16,
    paddingBottom: 96,
  },
  card: {
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
  },
  muted: {
    color: "#64748b",
  },
  roleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  roleInput: {
    flex: 1,
  },
  divider: {
    marginVertical: 12,
  },
  chips: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
  },
  chip: {
    marginRight: 4,
    marginBottom: 4,
  },
  permissionBlock: {
    marginTop: 14,
  },
  permissionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
  },
  permissionActions: {
    flexDirection: "row",
  },
  accordionBody: {
    paddingLeft: 16,
    paddingRight: 4,
    paddingBottom: 12,
  },
  fieldBody: {
    paddingLeft: 16,
    paddingRight: 4,
    paddingBottom: 12,
  },
  statusBlock: {
    marginTop: 14,
    padding: 12,
    borderRadius: 8,
    backgroundColor: "#f8fafc",
  },
  footer: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    padding: 16,
    backgroundColor: "#fff",
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: "#cbd5e1",
    alignItems: "center",
  },
});
